import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  TRIAL_REVIEWS,
  REVIEW_ROTATE_MS,
  type CustomerReview,
} from '@/lib/billing/trialReviews';
import { cn } from '@/lib/utils';

/**
 * The rotating customer-review card inside the plan-expiry gate.
 *
 * Auto-advances, with dots below that are real buttons — a dot is the only way
 * back to a review you just missed, so they must be operable, not decoration.
 *
 * Three behaviours are deliberate:
 *  - PAUSE on hover/focus. The quotes run to four lines; rotating out from under
 *    someone mid-sentence is the classic carousel failure.
 *  - RESPECT prefers-reduced-motion by not auto-advancing at all. Motion the
 *    user cannot stop is exactly what that setting exists to prevent; the dots
 *    still work, so no content becomes unreachable.
 *  - A CONSTANT card height. Every quote is rendered into one grid cell so the
 *    card is always as tall as the longest of them. The three reviews differ by
 *    about two lines, and anything less than this — a min-height, a hardcoded
 *    px value — lets the card grow and shrink on each rotation.
 */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    // Safari < 14 only has the deprecated listener API.
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return reduced;
}

export interface CustomerReviewCarouselProps {
  reviews?: readonly CustomerReview[];
  /** Override the dwell time; 0 disables auto-advance. */
  intervalMs?: number;
  className?: string;
}

export function CustomerReviewCarousel({
  reviews = TRIAL_REVIEWS,
  intervalMs = REVIEW_ROTATE_MS,
  className,
}: CustomerReviewCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const timerRef = useRef<number | null>(null);

  const total = reviews.length;
  const safeIndex = total > 0 ? index % total : 0;
  const current = reviews[safeIndex];

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearTimer();
    if (reducedMotion || paused || total < 2 || intervalMs <= 0) return;
    timerRef.current = window.setInterval(
      () => setIndex((i) => (i + 1) % total),
      intervalMs,
    );
    return clearTimer;
  }, [reducedMotion, paused, total, intervalMs, clearTimer]);

  /** Picking a dot restarts the dwell, so the chosen review gets a full turn. */
  const select = useCallback(
    (next: number) => {
      clearTimer();
      setIndex(next);
    },
    [clearTimer],
  );

  if (!current) return null;

  return (
    <div className={className}>
      <div
        className="relative overflow-hidden rounded-2xl border border-[#EAECF0] bg-[#F9FAFB]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        // Announce the change, but never steal focus or interrupt a screen reader.
        aria-live="polite"
        aria-atomic="true"
      >
        <div className="relative px-5 py-4 text-left">
          <span
            aria-hidden
            className="block text-[28px] font-semibold leading-none text-[#0EB64F]"
          >
            &ldquo;
          </span>
          {/*
            All quotes are rendered into the SAME grid cell, so the container is
            always as tall as the LONGEST one and the card never resizes between
            slides. A min-height cannot do this: it only sets a floor, so the
            longest quote still pushes past it and the card grows and shrinks.

            Reserving the tallest measured height also beats a hardcoded px value,
            which would break the moment the copy, the font or the width changes.
          */}
          <div className="-mt-2 grid">
            {reviews.map((review) => {
              const active = review.id === current.id;
              return (
                <p
                  key={review.id}
                  data-testid={active ? 'customer-review-quote' : undefined}
                  aria-hidden={!active}
                  className={cn(
                    'col-start-1 row-start-1 text-[13.5px] leading-relaxed text-[#344054] transition-opacity duration-500',
                    active ? 'opacity-100' : 'pointer-events-none opacity-0',
                  )}
                >
                  {review.quote}
                </p>
              );
            })}
          </div>

          <div className="mt-3.5 flex items-center gap-2.5 border-t border-[#EAECF0] pt-3.5">
            {/* Photo when the reviewer has one, initials disc otherwise. */}
            {current.avatarUrl ? (
              <img
                src={current.avatarUrl}
                alt=""
                loading="lazy"
                className="h-8 w-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E7F7EC] text-[11px] font-semibold text-[#076A2D]">
                {current.initials}
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-[#101828]">
                {current.name}
              </span>
              <span className="block truncate text-[12px] text-[#667085]">
                {current.handle}
              </span>
            </span>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1.5">
          {reviews.map((review, i) => {
            const active = i === safeIndex;
            return (
              <button
                key={review.id}
                type="button"
                data-testid={`customer-review-dot-${i}`}
                aria-label={`Show review ${i + 1} of ${total}`}
                aria-current={active ? 'true' : undefined}
                onClick={() => select(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  active ? 'w-5 bg-[#0EB64F]' : 'w-1.5 bg-[#D0D5DD] hover:bg-[#98A2B3]',
                )}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CustomerReviewCarousel;
